import { Box, Flex, Image, Td, Tooltip, Tr } from '@chakra-ui/react';
import { ArrayElement } from '../types';
import { bigRationalToBigNumber } from '../utils';
import { BigRational } from 'big-rational-ts';
import BigNumber from 'bignumber.js';
import { Balance, assetToUnit } from '../blockchain/client';
import { formatState } from '../blockchain/client/interact';
import StatusDot from '../components/atoms/svg/statusDot-svg';
import { IPortfolioState } from '../types';
import { stringToFormattedNumber } from './stringToNumber';

const abs = (n: bigint) => (n < 0n ? -n : n);

function SwapItem(props: {
  item: ArrayElement<IPortfolioState['assets']>;
  balance: Balance | null;
  type: 'mint' | 'burn';
  amountsToModify: Record<string, bigint>;
  formattedState: ReturnType<typeof formatState>;
  fontSize?: string;
  swapDeficit: boolean;
}) {
  const brZero = new BigRational(0n, 1n);
  const { fontSize = '12px' } = props;
  // Portfolio metrics before tx
  const adaPortfolioWorth = Object.entries(
    props.formattedState.state.assets,
  ).reduce(
    (acc, [asset, amount]) => {
      const assetWorth = new BigRational(amount, 1n).mul(
        props.formattedState.prices[asset],
      );
      return acc.add(assetWorth).reduce();
    },
    new BigRational(0n, 1n),
  );

  const adaWorthInPortfolio = props.formattedState.prices[props.item.asset.id]
    .mul(
      new BigRational(
        props.formattedState.state.assets[props.item.asset.id],
        1n,
      ),
    )
    .reduce();
  const tokensInPortfolio =
    props.formattedState.state.assets[props.item.asset.id];

  const worthPercentageInPortfolio = adaWorthInPortfolio
    .div(adaPortfolioWorth)
    .mul(new BigRational(100n, 1n).reduce());

  // Tx metrics
  const adaTxWorth = Object.entries(props.amountsToModify).reduce(
    (acc, [asset, amount]) => {
      const assetWorth = new BigRational(amount, 1n).mul(
        props.formattedState.prices[asset],
      );
      return acc.add(assetWorth).reduce();
    },
    new BigRational(0n, 1n),
  );
  const adaWorthInTx = props.formattedState.prices[props.item.asset.id]
    .mul(new BigRational(props.amountsToModify[props.item.asset.id], 1n))
    .reduce();
  const worthPercentageInTx = adaTxWorth.eq(brZero)
    ? brZero
    : adaWorthInTx.div(adaTxWorth).mul(new BigRational(100n, 1n)).reduce();
  const tokensInTx = props.amountsToModify[props.item.asset.id];

  // Portfolio metrics after tx
  const worthOfPortfolioAfterTx = adaTxWorth.add(adaPortfolioWorth).reduce();
  const adaWorthAfterTx = adaWorthInPortfolio.add(adaWorthInTx).reduce();
  const worthPercentageAfterTx = worthOfPortfolioAfterTx.eq(brZero)
    ? brZero
    : adaWorthAfterTx
        .div(worthOfPortfolioAfterTx)
        .mul(new BigRational(100n, 1n))
        .reduce();
  const tokensAfterTx = tokensInPortfolio + tokensInTx;

  const amountToModify = abs(
    props.amountsToModify[props.item.asset.id] || 0n,
  ).toString();

  const hasEnoughToken = BigNumber(
    (props.amountsToModify[props.item.asset.id] || 0n).toString(),
  ).lte(
    BigNumber(
      (props.balance?.[assetToUnit(props.item.asset)] || 0n).toString(),
    ),
  );

  const swapDeficit = props.swapDeficit;
  const balanceAmount = BigNumber(
    (props.balance?.[assetToUnit(props.item.asset)] || 0n).toString(),
  );
  const depositAmount = BigNumber(amountToModify.toString());

  const adjustedBalance = swapDeficit
    ? BigNumber.max(depositAmount.minus(balanceAmount), 0)
    : depositAmount;

  const pricePerToken = new BigNumber(props.item.price);

  function calculateBalanceValue(
    balanceAmount: BigNumber,
    pricePerToken: BigNumber,
  ): BigNumber {
    return balanceAmount
      .multipliedBy(pricePerToken)
      .div(new BigNumber(10).pow(props.item.asset.decimals)); // Adjust for decimals here
  }

  const balanceValue = calculateBalanceValue(balanceAmount, pricePerToken);

  function calculatedSwapValue(
    adjustedBalance: BigNumber,
    pricePerToken: BigNumber,
  ): BigNumber {
    return adjustedBalance
      .multipliedBy(pricePerToken)
      .div(new BigNumber(10).pow(props.item.asset.decimals));
  }

  const swapValue = calculatedSwapValue(adjustedBalance, pricePerToken);

  return (
    <Tr paddingX="0.5rem">
      <Td
        textAlign="left"
        flexDir={'row'}
        width={'fit-content'}
        paddingX={2}
        paddingY={3}
        alignContent="flex-start"
      >
        <Flex justify="left" gap={2}>
          <Box
            width="1.5rem"
            height="1.5rem"
            overflow="hidden"
            borderRadius="full"
            background="var(--metera-purple-200)"
          >
            <Image
              boxSize="100%"
              objectFit="cover"
              src={props.item.asset.imageUrl}
              alt={props.item.asset.ticker}
            />
            -
          </Box>
          <Flex flexDir="column">
            <Box fontSize="0.875rem" color="var(--primary-50)">
              {props.item.asset.ticker}
            </Box>
            <Flex gap={1} justify="right">
              <Box color="var(--neutral-400)" fontSize="0.7rem">
                ≈ ₳{BigNumber(props.item.price).toFixed(4)}
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Td>
      <Td
        paddingX={0}
        paddingY={3}
        alignContent="flex-start"
        justifyContent="flex-end"
      >
        <Flex flexDir="column" align="flex-start">
          <PortfolioDataItem
            fontSize="0.875rem"
            color="var(--primary-50)"
            value={BigNumber(amountToModify.toString())}
            decimals={props.item.asset.decimals}
          />
          <Flex gap={0} justify="right">
            <Box color="var(--neutral-400)" fontSize="0.7rem">
              ≈ ₳
            </Box>
            <PortfolioDataItem
              fontSize="0.7rem"
              color="var(--neutral-400)"
              value={bigRationalToBigNumber(adaWorthInTx).abs()}
              decimals={0}
            />
          </Flex>
        </Flex>
      </Td>
      <Td
        fontSize="0.875rem"
        paddingX={0}
        paddingY={3}
        alignContent="flex-start"
        justifyContent="center"
        textAlign="center"
      >
        <Flex flexDir="column" alignContent="center" justify="center">
          <Flex gap={1} justify="center">
            {hasEnoughToken ? (
              <StatusDot showStatus={false} status="Confirmed" />
            ) : (
              <StatusDot showStatus={false} status="Cancelled" />
            )}
            <PortfolioDataItem
              fontSize="0.875rem"
              value={balanceAmount}
              decimals={props.item.asset.decimals}
              color="var(--primary-50)"
            />
          </Flex>
          <Box color="var(--neutral-400)" fontSize="0.7rem">
            ≈ ₳{balanceValue.toFixed(2)}
          </Box>
        </Flex>
      </Td>
      <Td alignContent="flex-start" fontSize="0.875rem" paddingY={3}>
        <Flex flexDir="column" align="flex-end">
          <Flex>
            {}
            {stringToFormattedNumber(
              adjustedBalance.div(10 ** props.item.asset.decimals).toString(),
            )}
          </Flex>
          <Box color="var(--neutral-400)" fontSize="0.7rem">
            ≈ ₳{swapValue.toFixed(2)}
          </Box>
        </Flex>
      </Td>
    </Tr>
  );
}

const PortfolioDataItem = (props: {
  title?: string;
  value: BigNumber;
  color?: string;
  decimals: number;
  fontSize?: string;
}) => (
  <Flex flexDir={'column'} whiteSpace="nowrap">
    <Box color={props.color} fontSize={props.fontSize}>
      {stringToFormattedNumber(
        props.value.div(10 ** props.decimals).toString(),
      )}
    </Box>
  </Flex>
);

export default SwapItem;
